import React from 'react'
import { CSSObject } from '@emotion/react'

import {
  Box,
  Color,
  Display,
  FontWeight,
  ResponsiveValue,
  Text,
  TextAlign,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'

import { DiscountBox } from '../AboveTheFoldSection/components/DiscountBanner/config'

export type CWCDiscountPillsProps = {
  discountBoxes: DiscountBox[]
  // When a price is passed, the pills sit inside the grey container with the "Only £x £y per meal…"
  // line below (the Hero look). Omit it for the pills-only banner variant (First Box) — no container,
  // no price line. Keying the container off price presence keeps a single component serving both.
  price?: {
    pricePerPortion: string
    discountedPricePerPortion: string
  }
  // Sizes the outer element so each caller can width the pills (Hero container 342 / First Box 318).
  width?: ResponsiveValue<string>
}

// Repeat one value across all four breakpoints so it's emitted as media rules (see the discount
// text note below) — Citrus typography composes a @media(min-width:768px) size default that
// overrides a plain `fontSize`, so custom sizes must be media-wrapped to win.
const px4 = (v: string): [string, string, string, string] => [v, v, v, v]

// Outer "Discount row" container — its translucent bg is visible wrapping the pills box + price
const DiscountContainer = withCitrusTheme(Box, (theme) => [
  {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '10px',
    padding: '8px 12px',
    backgroundColor: 'rgba(26, 32, 38, 0.72)',
    borderRadius: '8px',
  } as CSSObject,
  theme.responsiveStyle(['100%', '342px', '342px'], ['width']) as CSSObject,
  // Desktop separation between header and offer groups is the 80px wrapper gap (see ContentWrapper),
  // so no extra top margin there; mobile/tablet keep the 10px for their space-between layout.
  theme.responsiveStyle(['0px', '10px', '10px', '0px'], ['marginTop']) as CSSObject,
])

// Inner pills box ("Frame 36473") — the segments sit in this, slightly smaller than the container
const PillsBox = withCitrusTheme(Box, () => ({
  display: 'flex',
  flexDirection: 'row',
  width: '100%',
  backgroundColor: '#29333B',
  borderRadius: '8px',
  overflow: 'hidden',
}))

const RedSegment = withCitrusTheme(Box, () => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '2px',
  // Segments share the pill width equally; minWidth:0 lets one shrink below its (nowrap) label's
  // min-content, so a four-tier discountBoxes config with longer labels stays visible instead of
  // PillsBox's overflow:hidden cutting off whole trailing tiers on narrow phones. overflow:hidden
  // clips any over-long label at *this segment's* edge so it can't spill over the adjacent tier.
  flex: '1 1 0',
  minWidth: 0,
  overflow: 'hidden',
  height: '60px',
  padding: '10px 8px',
}))

const GlassSegment = withCitrusTheme(Box, () => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '2px',
  flex: '1 1 0',
  minWidth: 0,
  overflow: 'hidden',
  height: '60px',
  padding: '10px 8px 4px',
  // colour/background/inverted-lighter per Figma 212:213 (was rgba(94,114,131,0.45))
  backgroundColor: '#516272',
  backdropFilter: 'blur(7px)',
  borderLeft: '1px solid rgba(255, 255, 255, 0.1)',
}))

// Citrus `Text` composes its own size as a @media(min-width:768px) rule into the same emotion
// class, so a plain `fontSize` here is overridden at tablet/desktop (renders 18px). The custom
// discount sizes must be emitted via responsiveStyle — same value across breakpoints — so they
// win by source order at every breakpoint (a plain fontSize silently loses to the Citrus media default).
// line-height is tightened to the font size (Citrus's 28px default otherwise inflates the segment
// past the design's 60px height) — also media-wrapped so it wins the same way as fontSize.
// Small phones (≤420px) get a ~10% smaller pill text so the tiers don't crowd/wrap on e.g. iPhone
// SE (375px). Emitted as a trailing max-width media rule so it wins source-order over the base sizes.
const smallPhonePillSize = (fontSize: string, lineHeight: string): CSSObject => ({
  '@media (max-width: 420px)': { fontSize, lineHeight },
})

const PrimaryDiscountPercentText = withCitrusTheme(Text, (theme) => [
  { letterSpacing: '-0.46px' } as CSSObject,
  theme.responsiveStyle(px4('23px'), ['fontSize']) as CSSObject,
  theme.responsiveStyle(px4('23px'), ['lineHeight']) as CSSObject,
  smallPhonePillSize('20px', '20px'),
])

const SecondaryDiscountPercentText = withCitrusTheme(Text, (theme) => [
  { letterSpacing: '-0.36px' } as CSSObject,
  theme.responsiveStyle(px4('18px'), ['fontSize']) as CSSObject,
  theme.responsiveStyle(px4('18px'), ['lineHeight']) as CSSObject,
  smallPhonePillSize('16px', '16px'),
])

// "OFF" is 11px on the primary (50%) segment, 9.5px on the secondary (40%/20%) segments.
const OffText = withCitrusTheme(Text, (theme) => [
  { fontWeight: 700, letterSpacing: '0.44px', opacity: 0.7 } as CSSObject,
  theme.responsiveStyle(px4('11px'), ['fontSize']) as CSSObject,
  theme.responsiveStyle(px4('11px'), ['lineHeight']) as CSSObject,
  smallPhonePillSize('10px', '10px'),
])

const SecondaryOffText = withCitrusTheme(Text, (theme) => [
  { fontWeight: 700, letterSpacing: '0.38px', opacity: 0.7 } as CSSObject,
  theme.responsiveStyle(px4('9.5px'), ['fontSize']) as CSSObject,
  theme.responsiveStyle(px4('9.5px'), ['lineHeight']) as CSSObject,
  smallPhonePillSize('8.5px', '8.5px'),
])

const DiscountLabelText = withCitrusTheme(Text, (theme) => [
  { fontWeight: 700, opacity: 0.9, whiteSpace: 'nowrap' } as CSSObject,
  theme.responsiveStyle(px4('10.5px'), ['fontSize']) as CSSObject,
  theme.responsiveStyle(px4('normal'), ['lineHeight']) as CSSObject,
  smallPhonePillSize('9.5px', 'normal'),
])

const PriceText = withCitrusTheme(Text, (theme) => ({
  color: theme.color(Color.White),
}))

// The tiered-discount pill row (one segment per discountBoxes entry — first is the accent "red"
// segment, the rest glass). Discount tiers are prop-driven (no value hardcoded). When a price is
// given it renders inside the grey container with the per-meal price line (Hero); without a price it
// renders pills-only (First Box banner). Pricing is passed in so the pricing source stays in the parent.
export function CWCDiscountPills({ discountBoxes, price, width }: CWCDiscountPillsProps) {
  const pills = (
    <PillsBox>
      {discountBoxes.map((box, index) => {
        const Segment = index === 0 ? RedSegment : GlassSegment
        const segmentBg = index === 0 ? { bg: box.bgColor } : {}
        const PercentText = index === 0 ? PrimaryDiscountPercentText : SecondaryDiscountPercentText
        const SuffixText = index === 0 ? OffText : SecondaryOffText

        return (
          <Segment key={box.label} {...segmentBg}>
            <PercentText fontWeight={FontWeight.Bold} color={box.textColor}>
              {box.discountValue}{' '}
              <SuffixText display={Display.Inline} color={box.textColor}>
                {box.discountSuffix}
              </SuffixText>
            </PercentText>
            <DiscountLabelText color={box.textColor}>{box.label}</DiscountLabelText>
          </Segment>
        )
      })}
    </PillsBox>
  )

  if (!price) {
    return (
      <Box data-testid="discount-pills" width={width} maxWidth="100%">
        {pills}
      </Box>
    )
  }

  return (
    <DiscountContainer data-testid="discount-container" width={width}>
      {pills}
      <PriceText size={2} textAlign={TextAlign.Center}>
        Only <span style={{ textDecoration: 'line-through' }}>£{price.pricePerPortion}</span>{' '}
        <span style={{ fontWeight: FontWeight.Bold }}>£{price.discountedPricePerPortion}</span> per
        meal on your 1st box*
      </PriceText>
    </DiscountContainer>
  )
}
